import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../api/client";
import { useAuth } from "../context/AuthContext";

const MIN_AMOUNT = 10; // EUR

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// Public-facing version of the buy widget: shows a live quote using the
// unauthenticated /assets and /quote endpoints, but never touches KYC,
// orders, or payments. "Continuar" just sends the visitor to register,
// carrying their selection along so they don't have to redo it.
export default function PublicCalculator() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [assets, setAssets] = useState([]);
  const [symbol, setSymbol] = useState("");
  const [chain, setChain] = useState("");
  const [amount, setAmount] = useState("");
  const debouncedAmount = useDebouncedValue(amount, 500);

  const [quoteTokens, setQuoteTokens] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    client
      .get("/api/cryptopocket/assets")
      .then(({ data }) => {
        const list = data?.data || [];
        setAssets(list);
        if (list.length) {
          setSymbol(list[0].symbol);
          setChain(list[0].blockchains?.[0] || "");
        }
      })
      .catch(() => setError("No se pudieron cargar las criptomonedas disponibles."));
  }, []);

  useEffect(() => {
    const asset = assets.find((a) => a.symbol === symbol);
    if (asset && chain && !asset.blockchains.includes(chain)) {
      setChain(asset.blockchains?.[0] || "");
    }
  }, [symbol, assets]); // eslint-disable-line

  useEffect(() => {
    const amt = Number(debouncedAmount);
    if (!amt || amt < MIN_AMOUNT || !symbol || !chain) {
      setQuoteTokens(null);
      return;
    }
    let cancelled = false;
    setQuoteLoading(true);
    client
      .post("/api/cryptopocket/quote", { amount: amt, symbol, chain, paymentType: "card" })
      .then(({ data }) => {
        if (cancelled) return;
        setQuoteTokens(data?.data?.tokens ?? null);
        setTicking(true);
        setTimeout(() => setTicking(false), 450);
      })
      .catch(() => !cancelled && setQuoteTokens(null))
      .finally(() => !cancelled && setQuoteLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedAmount, symbol, chain]);

  const selectedAsset = assets.find((a) => a.symbol === symbol);

  const goToNext = () => {
    if (user) {
      // already logged in — go straight to the buy flow with selection preloaded
      navigate("/dashboard", {
        state: {
          presetSymbol: symbol,
          presetChain: chain,
          presetAmount: amount || undefined,
        },
      });
    } else {
      // guest — carry selection into register so it's preloaded after signup
      const params = new URLSearchParams({ symbol, chain, amount: amount || "" });
      navigate(`/register?${params.toString()}`);
    }
  };

  return (
    <div className="calc-card">
      <div className="calc-body">
        <div className="field-row">
          <div className="field">
            <label>Moneda</label>
            <select value={symbol} onChange={(e) => setSymbol(e.target.value)}>
              {assets.map((a) => (
                <option key={a.symbol} value={a.symbol}>
                  {a.name} · {a.symbol}
                </option>
              ))}
            </select>
          </div>
        <div className="field">
  <label>Red</label>
  <select value={chain} onChange={(e) => setChain(e.target.value)}>
    {(selectedAsset?.blockchains || []).map((c) => (
      <option key={c} value={c}>
        {c.charAt(0).toUpperCase() + c.slice(1)}
      </option>
    ))}
  </select>
</div>
        </div>

        <div className="field">
          <label>Monto a pagar</label>
          <div className="amount-input">
            <input
              inputMode="decimal"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ""))}
            />
            <span className="amount-suffix">EUR</span>
          </div>
          <small className="hint">Mínimo {MIN_AMOUNT} EUR</small>
        </div>

        <div className={`ticker ${ticking ? "is-ticking" : ""}`}>
          <span className="ticker-label">Recibes aprox.</span>
          <span className="ticker-value">
            {quoteLoading ? "···" : quoteTokens != null ? quoteTokens.toFixed(6) : "—"}
          </span>
          <span className="ticker-symbol">{symbol}</span>
        </div>

        {error && <p className="error-text">{error}</p>}

        <button className="btn btn-primary btn-block" onClick={goToNext}>
          Continuar
        </button>
        <p className="fine-print">
          {user
            ? "El método de pago, billetera de destino y comisión final se confirman en el siguiente paso."
            : "Crea tu cuenta gratis para verificar tu identidad y completar la compra. El método de pago, billetera de destino y comisión final se confirman dentro de la página segura de nuestro proveedor de pagos."}
        </p>
      </div>
    </div>
  );
}